import React, { useState } from 'react';
import { MediumPrimaryButton, MediumButton } from '../../../components/Buttons';
import { ModalContainer, ModalBody, ModalHeader, ModalFooter } from '../../../components/ModalElements';
import { Label, AutoFocusInput } from '../../../components/FormElements';
import MaxChars from '../../../components/MaxChars';
import ErrorMessage from '../../../components/ErrorMessage';
import trainingService from '../../../services/trainingService';
import windowService from '../../../services/windowService';
import { handleError } from '../../../utils/apiUtils';
import { Training } from '../models/training';
import { User } from '../../authentication';

const CloneTrainingModal = ({
  training,
  user,
  handleDismiss,
}: {
  training: Training;
  user: User;
  handleDismiss: () => void;
}) => {
  const [name, setName] = useState(training.owner !== user.lastSelectedAccount ? training.name : '');
  const [UIState, setUIState] = useState({ isSubmitting: false, isError: '' });

  const redirectToTraining = (id: string) => {
    switch (training.type) {
      case 'course':
        return windowService.redirectTo(`#/manage-training/build/courses/${id}`);
      case 'pathway':
        return windowService.redirectTo(`#/manage-training/build/pathways/${id}`);
      default:
        return handleError(new Error('Unable to redirect. Invalid training type.'));
    }
  };

  const cloneTraining = (newName: string) => {
    setUIState({ isSubmitting: true, isError: '' });
    trainingService
      .cloneTraining(training.type, training.id, newName, user.lastSelectedAccount, user.userId)
      .then(res => redirectToTraining(res.id))
      .catch(reason => {
        console.error(reason);
        setUIState({
          isSubmitting: false,
          isError:
            reason?.eventType === 'TaskListNameDuplicateError' || reason?.eventType === 'WorkflowNameDuplicateError'
              ? 'Sorry, that name is already taken'
              : 'Something went wrong. Please try again.',
        });
      });
  };

  const handleClearInput = () => {
    setName('');
    if (UIState.isError) setUIState({ isSubmitting: false, isError: '' });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (UIState.isError) setUIState({ isSubmitting: false, isError: '' });
    setName(e.target.value);
  };

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader data-qa-hook="copyTrainingCopyView">
        <h3>Customize Training</h3>
      </ModalHeader>
      <ModalBody>
        <h4>
          Make your own version of <strong>{training.name}</strong> exclusively for use within your organization.
        </h4>
        <div className="space-between">
          <Label htmlFor="name">Name</Label>
          <MaxChars maxChars={64} currentChars={name.length} />
        </div>
        <AutoFocusInput
          autoFocus={true}
          id="name"
          aria-label="name"
          placeholder={'Enter Name'}
          value={name}
          maxLength={64}
          onChangeHandler={handleOnChange}
          clearInput={handleClearInput}
          selectTextOnFocus={true}
        />
        {UIState.isError && <ErrorMessage>{UIState.isError}</ErrorMessage>}
      </ModalBody>
      <ModalFooter>
        <MediumPrimaryButton
          data-qa-hook="copyTrainingCopyTraining"
          onClick={() => cloneTraining(name)}
          disabled={UIState.isSubmitting || !name.length}
          operating={UIState.isSubmitting}
        >
          Customize
        </MediumPrimaryButton>
        <MediumButton data-qa-hook="cloneTrainingModalCancelButton" onClick={() => handleDismiss()}>
          Cancel
        </MediumButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default CloneTrainingModal;
